import { useMemo } from 'react';
import { Container, Box, Typography, CircularProgress } from '@mui/material';
import SouthIcon from '@mui/icons-material/South';
import { useCurrentHash } from '../hooks/hashs/useCurrentHash';
import { useScreenStore } from '../stores/useScreenStore';
import { useTripFiltersStore } from '../stores/useTripFiltersStore';
import { useLinesStore } from '../stores/useLinesStore';
import { useClientsStore } from '../stores/useClientsStore';
import { useOrderStore } from '../stores/useOrderStore';
import { useListModeStore } from '../stores/useListModeStore';
import { useHourFilterStore } from '../stores/useHourFilterStore';
import { useOperationalMapStore } from '../stores/useOperationalMapStore';
import LineCardsView from './LineCardsView';
import LineTableView from './LineTableView';
import TripCardsView from './TripCardsView';
import TripTableView from './TripTableView';
import TripFilters from './TripFilters';
import useInfinitePagination from '../hooks/pagination/useInfinitePagination';
import ExportButton from './ExportButton';
import ConnectionIndicator from './ConnectionIndicator';
import { useCurrentHashStore } from '../stores/hashs/useCurrentHashStore';
import useHashListData from './hashs/useHashListData';
import { useLayoutStore } from '../hooks/screen/useLayoutStore';
import { useLoadingStore, selectIsMainLoading } from '../stores/useLoadingStore';

function buildFilterKey(value) {

  if (value === null) {
    return '__ALL__';
  }

  if (!Array.isArray(value)) {
    return String(value ?? '');
  }

  return [...value]
    .sort()
    .join(',');

}

const List = () => {

  const { sortedItems } = useHashListData();

  const isMainLoading = useLoadingStore(selectIsMainLoading);

  const monitorMode = useScreenStore(state => state.monitorMode);

  const order = useOrderStore(state => state.order);
  const orderASC = useOrderStore(state => state.orderASC);
  
  const {
    hashElemment,
    hashTitle,
    hashColor,
    HashIconComponent
  } = useCurrentHash();

  const listMode = useListModeStore(state => state.listMode);

  const currentHash = useCurrentHashStore(state => state.currentHash);

  const loadingFooter = useLoadingStore(
    state => state.loadingFooter
  );

  const loadingFooterTxt = useLoadingStore(
    state => state.loadingFooterTxt
  );

  const hourRange = useHourFilterStore(state => state.hourRange);

  const situacoesSelecionadas = useTripFiltersStore(state => state.situacoesSelecionadas);
  const desempenhosSelecionados = useTripFiltersStore(state => state.desempenhosSelecionados);
  const sentidosSelecionados = useLinesStore(state => state.sentidosSelecionados);
  const linhasSelecionadas = useLinesStore(state => state.linhasSelecionadas);
  const clientesSelecionados = useClientsStore(state => state.clientesSelecionados);

  const resetKey = useMemo(() => {

    if (hashElemment === 'LineCard') {
      return [
        currentHash,
        order,
        orderASC,
        buildFilterKey(sentidosSelecionados),
        buildFilterKey(clientesSelecionados),
        buildFilterKey(linhasSelecionadas)
      ].join('|');
    }

    return [
      hourRange,
      currentHash,
      order,
      orderASC,
      buildFilterKey(situacoesSelecionadas),
      buildFilterKey(desempenhosSelecionados),
      buildFilterKey(sentidosSelecionados),
      buildFilterKey(linhasSelecionadas)
    ].join('|');

  }, [
    hashElemment,
    currentHash,
    order,
    orderASC,
    situacoesSelecionadas,
    desempenhosSelecionados,
    sentidosSelecionados,
    clientesSelecionados,
    linhasSelecionadas,
    hourRange
  ]);

  const {
    visibleItems,
    endOfListRef
  } = useInfinitePagination(
    sortedItems,
    listMode === 'list' ? 20 : 10,
    listMode === 'list' ? 20 : 10,
    resetKey
  );

  const isOperationalMapMode = useOperationalMapStore(
    state => state.isOperationalMapMode
  );

  const isCompactMode = useLayoutStore(
    state => state.isCompactMode
  );

  const hasMore = visibleItems.length < sortedItems.length;

  return(
      <Container
        disableGutters
        maxWidth={false}
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          marginTop: isCompactMode
            ? 0
            : monitorMode === 1
                ? 3
                : 0.2,
          padding: 0
        }}
      >

      {monitorMode === 1 && (
        <Box
            position="sticky"
            top={0}
            sx={(theme) => ({
              flexShrink: 0,
              display: 'flex',
              flexWrap: 'wrap',
              gap: .2,
              backgroundColor: theme.palette[hashColor]?.main,
              paddingY: 1,
              zIndex: 3,
              color: theme.palette.getContrastText(
                theme.palette[hashColor]?.main
              ),
              alignItems: 'center',
              pl: (isCompactMode && !isOperationalMapMode) ? 10 : 4,
              pr: isCompactMode ? 2 : 4
            })}
          >
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: .5,
              width: '100% !important'
            }}>
              <HashIconComponent sx={{ fontSize: 26 }} />
              <Typography variant={"h6"}>{hashTitle}:</Typography>
              <ExportButton />
            </Box>
            
            {hashElemment === 'TripCard' && (
              <TripFilters inheritColor />
            )}
        </Box>
      )}

      {hashElemment === 'LineCard' && listMode === 'list' && (
        <LineTableView
          lines={visibleItems}
          endOfListRef={endOfListRef}
          resetScrollKey={resetKey}
        />
      )}

      {hashElemment === 'LineCard' && listMode === 'cards' && (
        <LineCardsView
          lines={visibleItems}
          endOfListRef={endOfListRef}
          resetScrollKey={resetKey}
        />
      )}

      {hashElemment === 'TripCard' && listMode === 'list' && (
        <TripTableView
          trips={visibleItems}
          endOfListRef={endOfListRef}
          resetScrollKey={resetKey}
        />
      )}

      {hashElemment === 'TripCard' && listMode === 'cards' && (
        <TripCardsView
          trips={visibleItems}
          endOfListRef={endOfListRef}
          resetScrollKey={resetKey}
        />
      )}

      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          py: .5,
          px: 1,
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <Typography variant='overline'>
          <b>
            Exibindo {visibleItems.length} de {sortedItems.length} itens
          </b>
        </Typography>
        <ConnectionIndicator />
        {loadingFooter && (
          <Box sx={{display: 'flex', gap: .5, alignItems:'center'}}>
            <CircularProgress size={20} />
            <Typography variant='overline'>
              <b>
                {loadingFooterTxt}
              </b>
            </Typography>
          </Box>
        )}
        {hasMore && ! isMainLoading && (
          <SouthIcon fontSize="small"/>
        )}
      </Box>

    </Container>
  )

};

export default List;